import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DRAWING_MOBILE_BOTTOM_BAR } from "./drawingViewerConstants";
import { DRAWING_KINDS, drawingKindLabel } from "./drawingKinds";
import { drawingKindStyle } from "./drawingKindStyles";
import {
  filterDrawingsForMobileMenu,
  groupDrawingsByStructure,
  structureKeysForDrawing,
} from "./drawingViewerMobileMenu";
import {
  ArrowLeftIcon,
  ArrowsPointingOutIcon,
  Bars3Icon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CursorArrowRaysIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  InformationCircleIcon,
  MinusIcon,
  PencilIcon,
  PlusIcon,
  SquaresPlusIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  ArrowPathIcon,
  CircleStackIcon,
  Square3Stack3DIcon,
  CubeIcon,
  ViewfinderCircleIcon,
  ScaleIcon,
  CameraIcon,
  ChatBubbleLeftEllipsisIcon,
  EyeIcon,
  TrashIcon,
  MapIcon,
} from "@heroicons/react/24/outline";
import { buildDrawingViewerTitleMeta } from "./drawingStructureApi";
import { measureLabel } from "./drawingAnnotationUtils";
import {
  isInspectableModule,
  parseModuleMeta,
} from "./drawingObservationUtils";
import { TOOL_GROUPS } from "./drawing-viewer-tool-rail";
import { IMMERSIVE_VIEWER_MOBILE_MQ } from "../immersive-viewer-layout";
import { SheetShell } from "./drawing-mobile-sheet";

export { SheetShell } from "./drawing-mobile-sheet";
export { canShowDrawing3dFab } from "./drawingViewerMobileMenu";

export const DrawingViewer3dFab = ({ t, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-viewer-ui
    title={t("drawings.viewer.see_3d")}
    aria-label={t("drawings.viewer.see_3d")}
    className="absolute z-[36] inline-flex h-10 max-w-[calc(100%-1rem)] items-center gap-1 rounded-full border border-[#BFDCF7] bg-white/95 px-2.5 text-[12px] font-bold tracking-[-0.01em] text-[#0B3A78] shadow-[0_12px_24px_-8px_rgba(20,99,199,0.45)] backdrop-blur-sm hover:bg-[#EAF3FD] right-[max(0.5rem,env(safe-area-inset-right,0px))] top-[max(0.5rem,env(safe-area-inset-top,0px))]"
  >
    <CubeIcon className="h-4 w-4 shrink-0 text-[#1463C7]" />
    <span className="hidden min-[360px]:inline">{t("drawings.viewer.see_3d_short")}</span>
  </button>
);

export const useIsDrawingMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(IMMERSIVE_VIEWER_MOBILE_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(IMMERSIVE_VIEWER_MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isMobile;
};

const MODULE_ICONS = {
  arrow: ArrowPathIcon,
  circle: CircleStackIcon,
  rectangular: Square3Stack3DIcon,
  pen: PencilIcon,
  measure: ScaleIcon,
  photo: CameraIcon,
  note: ChatBubbleLeftEllipsisIcon,
};

export const DrawingViewerMobileTopBanner = ({
  t,
  drawing,
  isVersionMode,
  activeVersion,
  versions = [],
  onOpenDrawingPicker,
}) => {
  const { locationLine } = buildDrawingViewerTitleMeta({
    t,
    drawing,
    isVersionMode,
    activeVersion,
    versions,
  });

  return (
    <header className="z-30 flex min-h-11 min-w-0 max-w-full flex-shrink-0 items-center gap-1.5 overflow-hidden border-b border-[#e5e8ec] bg-white px-1.5 py-1 xl:hidden">
      <Link
        to="/dashboard/drawings"
        className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[10px] bg-gradient-to-br from-[#2b6ef0] to-[#1d4fb0] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_1px_2px_rgba(20,23,28,0.06)]"
        title={t("common.back")}
        aria-label={t("common.back")}
      >
        <ArrowLeftIcon className="h-5 w-5" />
      </Link>
      {drawing?.project?.id || drawing?.project_id ? (
        <Link
          to={`/dashboard/projects/${drawing.project?.id || drawing.project_id}`}
          className="shrink-0 rounded-[8px] border border-[#C9D6FA] bg-[#F3F6FF] px-2 py-1 text-[11px] font-semibold text-[#1F4BE0]"
        >
          {t("drawings.list_ui.go_to_project")}
        </Link>
      ) : null}

      <button
        type="button"
        onClick={onOpenDrawingPicker}
        className="flex min-h-10 min-w-0 flex-1 items-center gap-1.5 overflow-hidden rounded-[10px] border border-[#d6dae0] bg-[#f6f7f9] px-2 text-left active:bg-[#eef0f3]"
        aria-label={t("drawings.viewer.mobile.switch_drawing")}
        aria-haspopup="dialog"
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex min-w-0 items-center gap-1 text-[13px] font-semibold tracking-[-0.01em] text-[#14171c]">
            <span className="min-w-0 truncate">{drawing.title}</span>
            {isVersionMode && activeVersion && (
              <span className="hidden min-[400px]:inline-block flex-shrink-0 rounded-full bg-[#eaf1fe] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#1d4fb0]">
                {activeVersion.title}
              </span>
            )}
          </div>
          {locationLine ? (
            <div className="hidden min-[400px]:block truncate font-mono text-[10px] text-[#59616d]">
              {locationLine}
            </div>
          ) : null}
        </div>
        <span className="flex min-w-0 max-w-[42%] shrink items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1d4fb0] ring-1 ring-[#c9d6fa]">
          <span className="min-w-0 truncate">{t("drawings.viewer.mobile.menu")}</span>
          <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" />
        </span>
      </button>
    </header>
  );
};

export const DrawingViewerMobileBottomBar = ({
  t,
  tool,
  onToggleSelectPan,
  onOpenTools,
  onAdd,
  onOpenModules,
  onOpenMore,
  placingObservation,
  modulesOpen,
  toolsOpen,
  moreOpen,
  canEdit = true,
}) => {
  // Single Velg/Pan toggle (not two buttons): icon/label show current mode.
  const selectPanModeActive = tool === "select" || tool === "pan";
  const SelectPanIcon = tool === "pan" ? HandRaisedIcon : CursorArrowRaysIcon;
  const selectPanLabel =
    tool === "pan"
      ? t("drawings.viewer.mobile.pan_short")
      : t("drawings.viewer.mobile.select_short");

  // Share bar height with zoom dock / content padding (matches report/project mobile nav).
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const mq = window.matchMedia(IMMERSIVE_VIEWER_MOBILE_MQ);
    const sync = () => {
      document.documentElement.style.setProperty(
        "--mobile-nav-height",
        mq.matches ? DRAWING_MOBILE_BOTTOM_BAR : "0px"
      );
    };
    sync();
    mq.addEventListener("change", sync);
    return () => {
      mq.removeEventListener("change", sync);
      document.documentElement.style.setProperty("--mobile-nav-height", "0px");
    };
  }, []);

  const itemClass = (active) =>
    `flex min-h-[44px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-[10px] px-0.5 py-1 transition-colors ${
      active
        ? "bg-[#eaf1fe] text-[#1d4fb0] ring-1 ring-inset ring-[#2b6ef0]/35"
        : "text-[#59616d] active:bg-[#eef0f3]"
    }`;

  const labelClass = "max-w-full truncate text-center text-[10px] font-semibold leading-tight";

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[55] max-w-full border-t border-[#e5e8ec] bg-white/95 shadow-[0_-4px_14px_rgba(20,23,28,0.08)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/92 xl:hidden"
      style={{
        paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))",
      }}
      aria-label={t("drawings.viewer.mobile.nav_aria")}
    >
      <div className="flex items-stretch justify-around gap-0.5 px-1 pt-1">
        <button
          type="button"
          onClick={onToggleSelectPan}
          className={itemClass(selectPanModeActive && !toolsOpen && !modulesOpen && !moreOpen)}
          aria-label={selectPanLabel}
          aria-pressed={selectPanModeActive}
          title={t("drawings.viewer.mobile.select_pan_toggle")}
        >
          <SelectPanIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span className={labelClass}>{selectPanLabel}</span>
        </button>

        <button
          type="button"
          onClick={onOpenTools}
          className={itemClass(toolsOpen || (!selectPanModeActive && !modulesOpen && !moreOpen))}
          aria-label={t("drawings.viewer.mobile.tools")}
          aria-expanded={toolsOpen}
        >
          <WrenchScrewdriverIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span className={labelClass}>{t("drawings.viewer.mobile.tools")}</span>
        </button>

        {canEdit && (
          <button
            type="button"
            onClick={onAdd}
            className={itemClass(false)}
            aria-label={t("drawings.observation.add")}
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[#2b6ef0] text-white shadow-[0_2px_6px_rgba(43,110,240,0.35)]">
              <PlusIcon className="h-5 w-5" aria-hidden />
            </span>
            <span className={`${labelClass} text-[#1d4fb0]`}>
              {t("drawings.viewer.mobile.add_short")}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={onOpenModules}
          className={itemClass(modulesOpen)}
          aria-label={t("drawings.viewer.modules")}
          aria-expanded={modulesOpen}
        >
          <SquaresPlusIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span className={labelClass}>{t("drawings.viewer.modules")}</span>
        </button>

        <button
          type="button"
          onClick={onOpenMore}
          className={itemClass(moreOpen)}
          aria-label={t("drawings.viewer.mobile.more")}
          aria-expanded={moreOpen}
        >
          <EllipsisHorizontalIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span className={labelClass}>{t("drawings.viewer.mobile.more")}</span>
        </button>
      </div>
    </nav>
  );
};

export const DrawingViewerMobileModulesSheet = ({
  t,
  open,
  onClose,
  modules,
  selectedModule,
  pendingChanges,
  measureScale,
  onSelectModule,
  onDeleteModule,
  onCenterModule,
  onOpenModuleDetail,
  scrollTopRef,
  canEdit = true,
}) => {
  const scrollRef = useRef(null);

  const persistScroll = () => {
    if (scrollRef.current) {
      scrollTopRef.current = scrollRef.current.scrollTop;
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    return () => persistScroll();
  }, [open, scrollTopRef]);

  const handleClose = () => {
    persistScroll();
    onClose();
  };

  const handlePick = (module) => {
    persistScroll();
    onSelectModule(module);
    onCenterModule(module);
    onClose();
  };

  return (
    <SheetShell
      open={open}
      onClose={handleClose}
      title={t("drawings.viewer.modules")}
      scrollRef={scrollRef}
      restoreScrollTop={scrollTopRef.current}
      heightClass="top-[4%]"
      closeLabel={t("drawings.viewer.mobile.close")}
    >
      {modules.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 px-6 py-12 text-center text-[#969da8]">
          <Square3Stack3DIcon className="h-8 w-8 opacity-40" />
          <p className="m-0 text-[13px] leading-relaxed">
            {t("drawings.viewer.no_modules")}
          </p>
        </div>
      ) : (
        <ul>
          {modules.map((module, index) => {
            const Icon = MODULE_ICONS[module.module_type] || Square3Stack3DIcon;
            const isSelected = selectedModule?.id === module.id;
            const hasPending = !!pendingChanges[module.id];
            const meta = parseModuleMeta(module);
            const inspectable = isInspectableModule(module);
            const moduleTitle =
              meta.title ||
              module.deviation?.displayTitle ||
              module.deviation?.title ||
              t(`drawings.module.${module.module_type}`);

            return (
              <li key={module.id}>
                <div
                  className={`group relative flex items-center gap-2.5 border-b border-[#e5e8ec] px-3.5 py-1 ${
                    isSelected ? "bg-[#eaf1fe]" : ""
                  }`}
                >
                  {isSelected && (
                    <span className="absolute bottom-0 left-0 top-0 w-[3px] bg-[#2b6ef0]" />
                  )}
                  <button
                    type="button"
                    onClick={() => handlePick(module)}
                    className="flex min-h-[48px] min-w-0 flex-1 items-center gap-2.5 py-2 text-left"
                  >
                    <span
                      className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-[#eef0f3]"
                      style={{ color: module.color || "#14171c" }}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#14171c]">
                        <span
                          className="inline-block h-2 w-2 flex-shrink-0 rounded-[3px]"
                          style={{ background: module.color || "#14171c" }}
                        />
                        {moduleTitle}
                      </span>
                      <span className="mt-0.5 block font-mono text-[11px] text-[#59616d]">
                        {module.module_type === "measure"
                          ? `${measureLabel(
                              parseFloat(module.x_position) || 0,
                              parseFloat(module.y_position) || 0,
                              parseFloat(module.x2_position) || 0,
                              parseFloat(module.y2_position) || 0,
                              measureScale
                            )} mm`
                          : module.module_type === "pen"
                            ? t("drawings.module.pen")
                            : typeof meta.page === "number"
                              ? `${t("drawings.observation.page")} ${meta.page + 1}`
                              : module.deviation_id
                                ? `#${module.deviation?.number || module.deviation_id}`
                                : `x${Math.round(module.x_position || 0)} y${Math.round(module.y_position || 0)}`}
                        {hasPending ? ` · ${t("drawings.viewer.unsaved")}` : ""}
                      </span>
                    </span>
                    <span className="font-mono text-[10px] text-[#969da8]">
                      #{index + 1}
                    </span>
                  </button>
                  {inspectable && (
                    <button
                      type="button"
                      onClick={() => onOpenModuleDetail?.(module)}
                      className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-[8px] text-[#59616d] hover:bg-[#eaf1fe] hover:text-[#2b6ef0]"
                      title={t("drawings.observation.view_details")}
                      aria-label={t("drawings.observation.view_details")}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => onDeleteModule(module.id)}
                      className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-[8px] text-[#969da8] hover:bg-red-50 hover:text-red-600"
                      title={t("drawings.module.delete")}
                      aria-label={t("drawings.module.delete")}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SheetShell>
  );
};

export const DrawingViewerMobileToolsSheet = ({
  t,
  open,
  onClose,
  tool,
  onToolChange,
  zoom,
  onZoomChange,
  onFit,
  canEdit = true,
}) => {
  const scrollRef = useRef(null);
  // View-only users keep select/pan (navigation) but never see markup tools.
  const toolGroups = canEdit ? TOOL_GROUPS : TOOL_GROUPS.slice(0, 1);

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={t("drawings.viewer.mobile.tools")}
      scrollRef={scrollRef}
      heightClass="top-[18%]"
      closeLabel={t("drawings.viewer.mobile.close")}
    >
      <div className="space-y-3 px-3 pb-5 pt-1 sm:space-y-4">
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#969da8]">
            {t("drawings.viewer.mobile.zoom")}
          </div>
          <div className="flex items-center gap-2 rounded-[12px] border border-[#e5e8ec] bg-[#f6f7f9] p-1.5">
            <button
              type="button"
              onClick={() => onZoomChange?.(zoom * 0.8)}
              className="grid h-11 min-w-11 flex-1 place-items-center rounded-[10px] bg-white text-[#14171c] shadow-sm active:bg-[#eef0f3]"
              aria-label={t("drawings.viewer.zoom_out")}
              title={t("drawings.viewer.zoom_out")}
            >
              <MinusIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => onFit?.()}
              className="grid h-11 min-w-[72px] flex-[1.2] place-items-center rounded-[10px] bg-white px-2 font-mono text-[13px] font-semibold text-[#14171c] shadow-sm active:bg-[#eef0f3]"
              aria-label={t("drawings.viewer.fit_to_screen")}
              title={t("drawings.viewer.fit_to_screen")}
            >
              {Math.round((zoom || 1) * 100)}%
            </button>
            <button
              type="button"
              onClick={() => onZoomChange?.(zoom * 1.25)}
              className="grid h-11 min-w-11 flex-1 place-items-center rounded-[10px] bg-white text-[#14171c] shadow-sm active:bg-[#eef0f3]"
              aria-label={t("drawings.viewer.zoom_in")}
              title={t("drawings.viewer.zoom_in")}
            >
              <PlusIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => onFit?.()}
              className="grid h-11 min-w-11 place-items-center rounded-[10px] bg-white text-[#14171c] shadow-sm active:bg-[#eef0f3]"
              aria-label={t("drawings.viewer.fit_to_screen")}
              title={t("drawings.viewer.fit_to_screen")}
            >
              <ArrowsPointingOutIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="mt-2 px-0.5 text-[11px] leading-relaxed text-[#969da8]">
            {t("drawings.viewer.mobile.pinch_hint")}
          </p>
        </div>

        <div className="h-px bg-[#e5e8ec]" />

        {toolGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            <div className="grid grid-cols-2 gap-2">
              {group.map(({ id, icon: Icon, tip, kbd }) => {
                const active = tool === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      onToolChange(id);
                      onClose();
                    }}
                    className={`flex min-h-[48px] items-center gap-2.5 rounded-[12px] border px-3 text-left transition-colors ${
                      active
                        ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0]"
                        : "border-[#e5e8ec] bg-white text-[#14171c] hover:bg-[#f6f7f9]"
                    }`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold">
                        {t(tip)}
                      </span>
                      <span className="font-mono text-[10px] text-[#969da8]">
                        {kbd}
                      </span>
                    </span>
                    {active && <CheckIcon className="h-4 w-4 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
            {groupIndex < toolGroups.length - 1 && (
              <div className="mt-4 h-px bg-[#e5e8ec]" />
            )}
          </div>
        ))}
      </div>
    </SheetShell>
  );
};

const DrawingMenuRow = ({ t, item, currentId, onPick }) => {
  const active = String(item.id) === String(currentId);
  const kind = item.drawing_kind || "arkitekt";
  const style = drawingKindStyle(kind);

  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(item, active)}
        className={`flex min-h-[56px] w-full items-center gap-3 px-3.5 py-2.5 text-left ${
          active ? "bg-[#eaf1fe]" : "active:bg-[#f6f7f9]"
        }`}
      >
        <DocumentTextIcon
          className={`h-5 w-5 flex-shrink-0 ${
            active ? "text-[#2b6ef0]" : "text-[#969da8]"
          }`}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold text-[#14171c]">
            {item.title || item.displayTitle || `#${item.id}`}
          </span>
          <span
            className="mt-1 inline-flex rounded px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em]"
            style={{
              color: style.color,
              background: style.bg,
              border: `1px solid ${style.border}`,
            }}
          >
            {drawingKindLabel(t, kind)}
          </span>
        </span>
        {active ? (
          <CheckIcon className="h-5 w-5 flex-shrink-0 text-[#2b6ef0]" />
        ) : (
          <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-[#c2c8d0]" />
        )}
      </button>
    </li>
  );
};

export const DrawingViewerMobileDrawingPicker = ({
  t,
  open,
  onClose,
  drawings,
  currentId,
  loading,
  hasProject = true,
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState(null);
  const [expandedHouseIds, setExpandedHouseIds] = useState([]);

  const emptyMessage = hasProject
    ? t("drawings.viewer.mobile.no_sibling_drawings")
    : t("drawings.viewer.mobile.no_project_drawings");

  const filtered = useMemo(
    () => filterDrawingsForMobileMenu(drawings, { query, kind: kindFilter }),
    [drawings, query, kindFilter],
  );
  const { houses, andre } = useMemo(
    () => groupDrawingsByStructure(filtered),
    [filtered],
  );

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setKindFilter(null);
    const current = (drawings || []).find(
      (item) => String(item.id) === String(currentId),
    );
    const { houseId } = structureKeysForDrawing(current);
    const houseIds = (drawings || [])
      .map((item) => structureKeysForDrawing(item).houseId)
      .filter(Boolean);
    setExpandedHouseIds(
      houseId
        ? [houseId]
        : houseIds.length === 1
          ? [houseIds[0]]
          : houseIds.slice(0, 2),
    );
  }, [open, currentId, drawings]);

  const toggleHouse = (houseId) => {
    setExpandedHouseIds((current) =>
      current.includes(houseId)
        ? current.filter((id) => id !== houseId)
        : [...current, houseId],
    );
  };

  const pickDrawing = (item, active) => {
    onClose();
    if (!active) navigate(`/dashboard/drawings/${item.id}`);
  };

  const hasFilter = Boolean(query.trim() || kindFilter);
  const noMatches = !loading && drawings.length > 0 && filtered.length === 0;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={t("drawings.viewer.mobile.switch_drawing")}
      scrollRef={scrollRef}
      heightClass="top-[max(4.75rem,calc(env(safe-area-inset-top,0px)+4.5rem))] mx-[max(0.5rem,env(safe-area-inset-left,0px),env(safe-area-inset-right,0px))] mb-[max(0.5rem,env(safe-area-inset-bottom,0px))]"
      closeLabel={t("drawings.viewer.mobile.close")}
    >
      {loading ? (
        <p className="px-4 py-8 text-center text-[13px] text-[#59616d]">
          {t("hr.common.loading")}
        </p>
      ) : drawings.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-[13px] leading-relaxed text-[#59616d]">
            {emptyMessage}
          </p>
          <Link
            to="/dashboard/drawings"
            className="mt-4 inline-flex min-h-[48px] items-center justify-center rounded-[10px] border border-[#2b6ef0] bg-[#eaf1fe] px-4 text-[14px] font-semibold text-[#1d4fb0]"
            onClick={onClose}
          >
            {t("drawings.viewer.mobile.to_drawings_list")}
          </Link>
        </div>
      ) : (
        <>
          <div className="sticky top-0 z-10 min-w-0 max-w-full space-y-2 overflow-x-hidden border-b border-[#e5e8ec] bg-white px-3 pb-2.5 pt-1">
            <label className="flex min-h-12 items-center gap-2 rounded-[12px] border border-[#d6dae0] bg-[#f6f7f9] px-3">
              <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0 text-[#969da8]" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("drawings.viewer.mobile.search_placeholder")}
                className="min-w-0 flex-1 border-none bg-transparent text-[15px] outline-none"
                autoComplete="off"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="grid h-9 w-9 place-items-center text-[#59616d]"
                  aria-label={t("drawings.viewer.mobile.close")}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ) : null}
            </label>
            <div className="flex min-w-0 max-w-full gap-1.5 overflow-x-auto overscroll-x-contain pb-0.5">
              <button
                type="button"
                onClick={() => setKindFilter(null)}
                className={`flex-shrink-0 rounded-full px-3 py-2 text-[12px] font-semibold ${
                  !kindFilter
                    ? "bg-[#12161c] text-white"
                    : "bg-[#f1f3f6] text-[#4b5563]"
                }`}
              >
                {t("drawings.list_ui.filter_all_types")}
              </button>
              {DRAWING_KINDS.map((kind) => {
                const active = kindFilter === kind;
                const style = drawingKindStyle(kind);
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setKindFilter(active ? null : kind)}
                    className={`flex-shrink-0 rounded-full px-3 py-2 text-[12px] font-semibold ${
                      active ? "text-white" : "text-[#4b5563]"
                    }`}
                    style={
                      active
                        ? { background: style.color }
                        : { background: style.bg, color: style.color }
                    }
                  >
                    {drawingKindLabel(t, kind)}
                  </button>
                );
              })}
            </div>
          </div>

          {!hasProject && (
            <div className="border-b border-[#e5e8ec] px-3.5 py-3">
              <p className="m-0 text-[12px] leading-relaxed text-[#59616d]">
                {t("drawings.viewer.mobile.no_project_switch_hint")}
              </p>
              <Link
                to="/dashboard/drawings"
                className="mt-2 inline-flex min-h-11 items-center text-[14px] font-semibold text-[#2b6ef0]"
                onClick={onClose}
              >
                {t("drawings.viewer.mobile.to_drawings_list")}
              </Link>
            </div>
          )}

          {noMatches ? (
            <p className="px-4 py-8 text-center text-[13px] text-[#59616d]">
              {t("drawings.viewer.mobile.no_search_results")}
            </p>
          ) : (
            <>
              {houses.map((house) => {
                const expanded =
                  hasFilter || expandedHouseIds.includes(house.id);
                const count = house.floors.reduce(
                  (sum, floor) => sum + floor.items.length,
                  0,
                );
                return (
                  <section key={house.id}>
                    <button
                      type="button"
                      onClick={() => toggleHouse(house.id)}
                      className="flex min-h-12 w-full items-center gap-2 border-b border-[#e5e8ec] bg-[#f6f7f9] px-3.5 text-left"
                      aria-expanded={expanded}
                    >
                      <ChevronDownIcon
                        className={`h-4 w-4 flex-shrink-0 text-[#59616d] transition-transform ${
                          expanded ? "" : "-rotate-90"
                        }`}
                      />
                      <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-[#14171c]">
                        {house.name || t("drawings.house")}
                      </span>
                      <span className="font-mono text-[12px] text-[#969da8]">
                        {count}
                      </span>
                    </button>
                    {expanded
                      ? house.floors.map((floor) => (
                          <div key={floor.id}>
                            <div className="border-b border-[#eef0f3] bg-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.04em] text-[#969da8]">
                              {t("drawings.floor")} {floor.name || floor.id}
                            </div>
                            <ul>
                              {floor.items.map((item) => (
                                <DrawingMenuRow
                                  key={item.id}
                                  t={t}
                                  item={item}
                                  currentId={currentId}
                                  onPick={pickDrawing}
                                />
                              ))}
                            </ul>
                          </div>
                        ))
                      : null}
                  </section>
                );
              })}
              {andre.length > 0 && (
                <section>
                  <div className="border-y border-[#e5e8ec] bg-[#f6f7f9] px-3.5 py-2.5 text-[11px] font-bold uppercase tracking-[0.04em] text-[#969da8]">
                    {t("drawings.structure.andre")}
                  </div>
                  <ul>
                    {andre.map((item) => (
                      <DrawingMenuRow
                        key={item.id}
                        t={t}
                        item={item}
                        currentId={currentId}
                        onPick={pickDrawing}
                      />
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </>
      )}
    </SheetShell>
  );
};

export const DrawingViewerMobileMoreSheet = ({
  t,
  open,
  onClose,
  pendingCount,
  saving,
  onSaveChanges,
  onEdit,
  onOpenPanelTab,
  onStartAddDeviation,
  isVersionMode,
  onSwitchVersion,
  onCreateVersion,
  creatingVersion,
  onStartCalibration,
  canEdit = true,
  onOpen3d,
  onAlign3d,
  aligning3d,
}) => {
  const scrollRef = useRef(null);

  const rows = [
    {
      id: "save",
      label:
        pendingCount > 0
          ? `${t("drawings.save_changes")} (${pendingCount})`
          : t("common.edit"),
      icon: pendingCount > 0 ? CheckIcon : PencilIcon,
      onClick: () => {
        onClose();
        if (pendingCount > 0) onSaveChanges();
        else onEdit();
      },
      primary: pendingCount > 0,
      disabled: saving,
      hidden: !canEdit,
    },
    {
      id: "details",
      label: t("drawings.viewer.details"),
      icon: InformationCircleIcon,
      onClick: () => {
        onClose();
        onOpenPanelTab("details");
      },
    },
    {
      id: "deviations",
      label: t("drawings.viewer.deviations"),
      icon: ExclamationTriangleIcon,
      onClick: () => {
        onClose();
        // Same place-flow as Legg til → Avvik (create form → pin on canvas).
        // View-only users can't create, so just open the deviations list.
        if (canEdit && onStartAddDeviation) onStartAddDeviation();
        else onOpenPanelTab("deviations");
      },
    },
    {
      id: "checklists",
      label: t("drawings.viewer.checklists"),
      icon: Bars3Icon,
      onClick: () => {
        onClose();
        onOpenPanelTab("checklists");
      },
    },
    {
      id: "structure",
      label: t("drawings.structure.title"),
      icon: MapIcon,
      onClick: () => {
        onClose();
        onOpenPanelTab("structure");
      },
    },
    {
      id: "calibrate",
      label: t("drawings.viewer.calibrate"),
      icon: ScaleIcon,
      onClick: () => {
        onClose();
        onStartCalibration();
      },
      hidden: !canEdit,
    },
    ...(onOpen3d
      ? [
          {
            id: "see3d",
            label: t("drawings.viewer.see_3d"),
            icon: CubeIcon,
            onClick: () => {
              onClose();
              onOpen3d();
            },
          },
        ]
      : []),
    ...(onAlign3d
      ? [
          {
            id: "align3d",
            label: t("drawings.viewer.align_3d"),
            icon: ViewfinderCircleIcon,
            onClick: () => {
              onClose();
              onAlign3d();
            },
            disabled: aligning3d,
          },
        ]
      : []),
    {
      id: "live",
      label: t("drawings.viewer.live_drawing"),
      icon: DocumentTextIcon,
      onClick: () => {
        onClose();
        onSwitchVersion(null);
      },
      hidden: !isVersionMode,
    },
    {
      id: "version",
      label: t("drawings.viewer.create_version"),
      icon: SquaresPlusIcon,
      onClick: () => {
        onClose();
        onCreateVersion();
      },
      disabled: creatingVersion,
      hidden: !canEdit,
    },
  ].filter((row) => !row.hidden);

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={t("drawings.viewer.mobile.more")}
      scrollRef={scrollRef}
      heightClass="top-[20%]"
      closeLabel={t("drawings.viewer.mobile.close")}
    >
      <ul className="px-3 pb-5 pt-1">
        {rows.map(({ id, label, icon: Icon, onClick, primary, disabled }) => (
          <li key={id} className="mb-1.5">
            <button
              type="button"
              onClick={onClick}
              disabled={disabled}
              className={`flex min-h-[48px] w-full items-center gap-3 rounded-[12px] border px-3 text-left transition-colors disabled:opacity-50 ${
                primary
                  ? "border-[#2b6ef0] bg-[#2b6ef0] text-white"
                  : "border-[#e5e8ec] bg-white text-[#14171c] hover:bg-[#f6f7f9]"
              }`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-[13px] font-semibold">{label}</span>
            </button>
          </li>
        ))}
      </ul>
    </SheetShell>
  );
};
